// apps/client/src/pages/LegalGatingScreen.tsx
//
// Legal acceptance fallback — shown if user skips onboarding.
// Uses iOS-style toggles, all English copy, purple aesthetic.
//

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../store/userStore';
import Icon from '../ui-kit/Icon';
import { Toggle } from '../ui-kit/Toggle';

export function LegalGatingScreen() {
    const { t } = useTranslation();
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [showError, setShowError] = useState(false);
    const acceptLegal = useUserStore((s) => s.acceptLegal);

    const canContinue = acceptTerms && acceptPrivacy;

    const handleContinue = () => {
        if (!canContinue) {
            setShowError(true);
            return;
        }
        acceptLegal();
    };

    return (
        <div className="min-h-[100dvh] bg-purple-gradient flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-sm">
                {/* Icon */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 rounded-3xl bg-white/10 border border-white/15 flex items-center justify-center mx-auto mb-4">
                        <Icon name="gavel" className="text-4xl text-primary" />
                    </div>
                    <h1 className="text-2xl font-black text-white mb-2 font-display">{t('legal.title')}</h1>
                    <p className="text-white/50 text-sm">{t('legal.subtitle')}</p>
                </div>

                {/* Toggles */}
                <div className={`space-y-3 mb-8 ${showError ? 'animate-shake' : ''}`}>
                    <Toggle
                        checked={acceptTerms}
                        onChange={(v) => { setAcceptTerms(v); setShowError(false); }}
                        label={<span>{t('legal.terms')} <a href="https://bingoton.app/privacy" target="_blank" className="underline font-black text-[#FFC107]">{t('legal.termsLink')}</a></span>}
                        description={t('legal.termsDesc')}
                        error={showError && !acceptTerms}
                    />
                    <Toggle
                        checked={acceptPrivacy}
                        onChange={(v) => { setAcceptPrivacy(v); setShowError(false); }}
                        label={<span>{t('legal.privacy')} <a href="https://bingoton.app/privacy" target="_blank" className="underline font-black text-[#FFC107]">{t('legal.privacyLink')}</a></span>}
                        description={t('legal.privacyDesc')}
                        error={showError && !acceptPrivacy}
                    />
                </div>

                {/* CTA */}
                <motion.button
                    whileTap={canContinue ? { scale: 0.95 } : { scale: 1 }}
                    onClick={handleContinue}
                    className={`w-full py-4 rounded-pill font-black text-lg uppercase tracking-wider transition-all ${canContinue
                        ? 'bg-gold-gradient text-[#1a0b2e] shadow-neon-gold'
                        : 'bg-white/10 text-white/30 cursor-not-allowed'
                        }`}
                >
                    {t('legal.continue')}
                </motion.button>
            </div>
        </div>
    );
}
